import { useEffect } from 'react';
import useStorage from './useStorage';
import { useTypedFastStorage } from './useFastStorage';
import postal from 'postal';

import useTableNavigation from '../ui/tables/useTableNavigation';
import useListHeightMonitor from './useListHeightMonitor';

type PagingOptions = {
  pageSize: number;
  pagePerBatch: number;
};

export default function useListData(name: string, onDoubleClick: any, tableColumnDefaultFocusHierarchy: Array<any>, onCellRightClick: ((row: number, column: number, node: Element, e: MouseEvent) => void) | undefined = undefined, initialized: boolean, setListDataWhenCellClick: any, pagingOptions: PagingOptions) {
  const channel = postal.channel('ifms-filter');
  useListHeightMonitor(name);

  const dataKeySufix = `${window.location.pathname}${window.location.search}`;

  const [pageNum, , , pageNumRef] = useTypedFastStorage<number>(`ifms-list-data-${name}-pagenum`, 1);

  const [data, _setData, removeDataFromStorage, dataRef] = useTypedFastStorage<Array<any>>(`ifms-list-data-${dataKeySufix}`, []);
  const [, _setSelectedId, removeSelectedIdFromStorage, selectedIdRef] = useStorage(`${name}-selectedId`, -1);
  const [currentCount, setCurrentCount, removeCurrentCount] = useStorage(`${name}-totalDataCount`, -1);
  const { selectTableRow, tableNavigationCleanup, triggerTableSearch } = useTableNavigation(name, onRowSelected, onDoubleClick, tableColumnDefaultFocusHierarchy || [], false, false, false, onDoubleClick, onCellRightClick, initialized, false, [pageNum, selectedIdRef.current]);

  useEffect(() => {
    if (!initialized || !pageNum) return;

    const blockStartPage = pageNum - ((pageNum - 1) % pagingOptions.pagePerBatch);
    const pageIndexInBlock = pageNum - blockStartPage;
    const rowIndex = pageIndexInBlock * pagingOptions.pageSize;

    onRowSelected(rowIndex);
  }, [pageNum]);

  function applyIdSelectionAlgorithm(data: any) {
    if (!data || data.length === 0 || !pageNumRef.current) {
      setSelectedId(null);
      return;
    }
  
    const pageNum = pageNumRef.current;

    const blockStartPage = pageNum - ((pageNum - 1) % pagingOptions.pagePerBatch);
    const pageIndexInBlock = pageNum - blockStartPage;

    const rowStart = pageIndexInBlock * pagingOptions.pageSize;
    const rowEnd = rowStart + pagingOptions.pageSize - 1;
  
    const safeRowIndex = Math.min(Math.max(rowStart, 0), data.length - 1);

    const existsInData = data.some((d: any) => d.id === selectedIdRef.current);
    const existsInCurrentPage = data.slice(rowStart, rowEnd + 1).some((d: any) => d.id === selectedIdRef.current);

    if (!existsInData || !existsInCurrentPage) {
      setSelectedId(data[safeRowIndex].id);
    }
  }

  function onRowSelected(index:number) {
    if(setListDataWhenCellClick) setListDataWhenCellClick();

    if (dataRef.current && index >= 0 && index < dataRef.current.length) {
      setSelectedId((dataRef.current[index] as any).id);
    }
  }

  function setData(d:any, count:number) {
    // Make sure it is not data merge but complete replace
    //removeDataFromStorage();
    setCurrentCount(count);
    _setData(d);
    applyIdSelectionAlgorithm(d);
    channel.publish('ifms-filter.countsetter', count);
  }

  function setSelectedId(id:any) {
    selectedIdRef.current = id;
    _setSelectedId(id);
  }

  // selectedDisplayIndex가 true면 화면상 인덱스를 반환
  function getSelectedIndex(selectedDisplayIndex: boolean = false) {
    if (!selectedIdRef?.current || !dataRef?.current || !pageNum) return -1;

    const globalIndex = dataRef.current.findIndex(
      (x: any) => x.id + '' === selectedIdRef.current + ''
    );
    if (globalIndex === -1) return -1;

    if (!selectedDisplayIndex) return globalIndex;

    const localIndex = globalIndex - (pageNum - 1) * pagingOptions.pageSize;

    return localIndex >= 0 && localIndex < pagingOptions.pageSize ? localIndex : -1;
  }

  function clearListData() {
    removeDataFromStorage();
    tableNavigationCleanup();
    removeSelectedIdFromStorage();
    removeCurrentCount();
  }

  return {
    data: data ?? [],
    dataRef,
    setData,
    setSelectedId,
    getSelectedIndex,
    clearListData,
    selectTableRow,
    triggerTableSearch,
    currentCount,
  };
}
  